// import 'core-js'
// import 'regenerator-runtime/runtime'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { i18nClient } from '@sailor/i18n-web'
import { setI18nUtilsReporter } from '@sailor/i18n-utils'
import ChainedBackend from 'i18next-chained-backend'
import OpenAPIBackend from '@sailor/i18n-web-openapi-backend'
import resourcesToBackend from 'i18next-resources-to-backend'
import resources from './locales/resources'
import Root from './Root'
import {
  isProd, getLanguage, getDirection, initCompass, getRegionByCookie, setRegion,
  getInWindowsApp,
} from '@RootCommon/utils'
import locale from '@roo/roo/locale'
import ConfigProvider from '@roo/roo/ConfigProvider'
import zhHK from '@roo/roo/locale/lang/zh-TW'
import zh from '@roo/roo/locale/lang/zh-CN'
import en from '@roo/roo/locale/lang/en-US'
import ar from '@roo/roo/locale/lang/ar'
import locale_plus from '@roo/roo-plus/locale'
import zhHK_plus from '@roo/roo-plus/locale/lang/zh-TW'
import zh_plus from '@roo/roo-plus/locale/lang/zh-CN'
import en_plus from '@roo/roo-plus/locale/lang/en-US'
import { configResponsive, initUtils } from '@RootCommon/utils/h5PC'
import { getQueryValues } from '@RootCommon/utils'
import { owlReportMetricsWeb } from '@RootCommon/utils/owl'
import { getResolutionInfo } from '@/common/utils'
import responsiveConfig from './common/constants/mediaBreakPoint.json'
import dayjs from 'dayjs'
import { RecoilRoot } from 'recoil'
import i18nConfig from '../.i18n.config.json'
import { registerSw } from './registerSw'
import { setCompassRegion } from '@sailor/compass'
import HyperCache from '@sailor/hyperCache'
import './common/utils/initFunction'
import { initPerformanceReport } from './common/utils/performanceReport'
import { initHorn } from '@RootCommon/utils/horn'
import { HORN_KEY, defaultHornConfig } from './common/constants/horn'
import { loganLog } from '@RootCommon/utils/logan'

// 初始化性能指标采集和上报
initPerformanceReport()

// 初始化 horn
initHorn(HORN_KEY, defaultHornConfig)

const isProdEnv = isProd()
const i18nEnv = isProdEnv ? 'prod' : 'test'
const region = getRegionByCookie()
const isWindowsApp = getInWindowsApp()

setCompassRegion(region)
setRegion(region)

// 初始化region相关信息，owl、LXAnalytics等
initCompass({})

owlReportMetricsWeb({ 'customDeviceInfo': 1 }, getResolutionInfo())

if (isProdEnv) {
  try {
    // st 和 线上 关闭， 观察后再看是否关闭 pwa
    // @ts-ignore
    const hyperCache = HyperCache.getInstance()
    hyperCache.turnOff() // HyperCache.turnOff()仅68版本可，69版本兼可
    console.log('hyperCache.turnOff success')
  } catch (error) {
    console.log('hyperCache.turnOff error:', error)
  }
} else {
  registerSw()
}

const rooLocaleLangs = {
  'zh-HK': zhHK,
  'zh-CN': zh,
  zh: zh,
  en: en,
  ar: ar,
  'pt-BR': en, // 暂无，使用英文
}

const rooPlusLocaleLangs = {
  'zh-HK': zhHK_plus,
  zh: zh_plus,
  en: en_plus,
  ar: en_plus, // TODO： 暂无，使用英文
  'pt-BR': en_plus, // 暂无，使用英文
}
configResponsive(responsiveConfig)
;(async () => {
  const fallbackLng = 'en'
  await initUtils()
  const i18nInitConfig: any = {
    // 设置回退语言
    fallbackLng,
    // supportedLngs: ['zh-HK', 'en', 'zh', 'ar'],
    ns: i18nConfig.namespaces.map((v: any) => v.name), // 当使用多个空间名称时，必须配置 ns，且第一个为默认空间名
    defaultNS: i18nConfig.namespaces.map((v: any) => v.name)[0], // 设置默认空间名称
    backend: {
      // @ts-ignore
      openI18nKeyRaptor: true,
      i18nKeyRaptorConfig: {
        // i18nKeyRaptorConfig：web 端上报相关配置
        // web 项目参数
        clientAppkey: 'com.sankuai.sailorfe.b.pc', // 项目 appkey，如 keeta M 端为：com.sankuai.sailorfe.b.pc
        region: getRegionByCookie(),
      },
      backends: [
        OpenAPIBackend, // 通过api获取最新语言包
        resourcesToBackend(resources), // 本地资源包作为兜底
      ],
      // 这里配置一下所使用的projectId和namespaceId
      backendOptions: [{ ...i18nConfig, env: i18nEnv }],
      // @ts-ignore
      cacheOptions: { openClearOldLanguagePackage: true },
      idc: process_env_publishIdc || 'HK', // 当前代码部署的机房
      useSailorCompass: true, // 配置后会使用罗盘获取 i18n web sdk 对应 host 域名
      autoFetchI18nApiConfig: true, // 配置后自动进行 fetchResourceJson，业务侧可以取消业务代码中的 fetchResourceJson
    },
    detection: {
      // 语言自动识别配置
      caches: ['sessionStorage', 'localStorage'],
      // order: ['querystring', 'cookie', 'sessionStorage', 'localStorage'],
    },
  }
  i18nClient.use(ChainedBackend).init(i18nInitConfig, async function () {
    const defaultLang = getLanguage() || fallbackLng
    // if (isWindowsApp) {
    //   const language = await window.electronAPI?.getLanguage?.()
    //   if (language !== defaultLang) {
    //     debugger
    //     const { origin, pathname, search, hash } = window.top.location
    //     const searchMap = qs.parse(search, { ignoreQueryPrefix: true })
    //     searchMap.locale = language
    //     const newSearch = `?${qs.stringify(searchMap)}`
    //     const _url = origin + pathname + newSearch + hash
    //     window.top.location.href = _url
    //   }
    // }
    dayjs.locale(defaultLang) // dayjs的语言名可能不一致
    // 回调中初始化App，并配合lazy的模块才能使用ReactNode外的i18n常量
    rooLocaleLangs[defaultLang] && locale.use(rooLocaleLangs[defaultLang])
    rooPlusLocaleLangs[defaultLang] && locale_plus.use(rooPlusLocaleLangs[defaultLang])
    createRoot(document.getElementById('root') as HTMLElement).render(
      <ConfigProvider config={{ direction: getDirection('main') }}>
        <RecoilRoot>
          <Root />
        </RecoilRoot>
      </ConfigProvider>,
    )
  })
})()
setI18nUtilsReporter({
  report: (eventName: string, msg: string, tag: Record<string, string>) => {
    loganLog({
      msg,
    })
    owlReportMetricsWeb({ [eventName]: 1 }, { ...tag })
  },
})
